import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api.js';

interface PanicMeal {
  id: string;
  name: string;
  mealType: string;
  cuisine: string | null;
  instructions: string[];
}

interface Props {
  onClose: () => void;
}

export function PanicModal({ onClose }: Props) {
  const { data, isLoading, isError, refetch } = useQuery<{ meals: PanicMeal[] }>({
    queryKey: ['panic-meals'],
    queryFn: () => api.get('/api/recipes/panic'),
    staleTime: 0,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 px-4 py-8" onClick={onClose}>
      <div
        className="bg-surface rounded-2xl shadow-xl w-full max-w-md p-6 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">What can I make right now?</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">Quick meals · ≤20 min</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl font-bold leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="space-y-3 animate-pulse">
            {[1].map((i) => <div key={i} className="h-14 bg-gray-200 dark:bg-gray-700 rounded-xl" />)}
          </div>
        ) : isError || !data?.meals.length ? (
          <div className="text-center py-6">
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
              {isError
                ? 'Could not load recipes. Try again.'
                : 'No quick recipes found. Try again in a bit.'}
            </p>
            <button
              onClick={() => refetch()}
              className="text-sm font-medium hover:underline"
              style={{ color: 'var(--accent)' }}
            >
              Try again →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {data.meals.map((meal) => (
              <div
                key={meal.id}
                className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{meal.name}</p>
                    {meal.cuisine && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 capitalize">{meal.cuisine}</p>
                    )}
                  </div>
                  <span className="text-xs bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full flex-shrink-0">≤20 min</span>
                </div>
                {meal.instructions.length > 0 && (
                  <ol className="mt-2 space-y-1 list-decimal list-inside">
                    {meal.instructions.slice(0, 3).map((step, i) => (
                      <li key={i} className="text-xs text-gray-600 dark:text-gray-400 leading-snug">{step}</li>
                    ))}
                    {meal.instructions.length > 3 && (
                      <li className="text-xs text-gray-400 dark:text-gray-500">+{meal.instructions.length - 3} more steps</li>
                    )}
                  </ol>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
